import { useState, useMemo, useRef } from "react";

// ─── Chip input for list values (categories, tags, branch names) ────────────
// Stored as string[]. Typing a comma or pressing Enter commits a chip, so a
// comma never has to survive in the raw input — which is what used to make
// these fields feel broken (every keystroke was re-split and re-joined).
export function TagsInput({ value, onChange, placeholder, examples = [], invalid }) {
    const list = useMemo(() => {
        if (Array.isArray(value)) return value.filter(Boolean);
        if (typeof value === "string" && value.trim()) return value.split(",").map(t => t.trim()).filter(Boolean);
        return [];
    }, [value]);

    const [draft, setDraft] = useState("");
    const inputRef = useRef(null);

    const commit = (raw) => {
        const parts = String(raw).split(",").map(t => t.trim()).filter(Boolean);
        setDraft("");
        if (!parts.length) return;
        const next = [...list];
        for (const p of parts) {
            if (!next.some(t => t.toLowerCase() === p.toLowerCase())) next.push(p);
        }
        if (next.length !== list.length) onChange(next);
    };

    const removeAt = (i) => onChange(list.filter((_, idx) => idx !== i));

    const unusedExamples = examples.filter(ex => !list.some(t => t.toLowerCase() === ex.toLowerCase()));

    return (
        <div>
            <div
                onClick={() => inputRef.current?.focus()}
                className={`w-full flex flex-wrap items-center gap-1.5 border rounded-lg px-2 py-1.5 bg-[#ffffff] dark:bg-[#181d33] cursor-text focus-within:ring-2 focus-within:ring-indigo-500 ${invalid ? "border-red-400 dark:border-red-500" : "dark:border-gray-600"}`}
            >
                {list.map((tag, i) => (
                    <span key={`${tag}-${i}`} className="inline-flex items-center gap-1 max-w-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs font-semibold rounded-md pl-2 pr-1 py-1">
                        <span className="truncate">{tag}</span>
                        <button
                            type="button"
                            onClick={e => { e.stopPropagation(); removeAt(i); }}
                            aria-label={`Remove ${tag}`}
                            className="text-indigo-400 hover:text-red-500 leading-none"
                        >×</button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={draft}
                    placeholder={list.length ? "" : placeholder}
                    onChange={e => {
                        const v = e.target.value;
                        if (v.includes(",")) commit(v);
                        else setDraft(v);
                    }}
                    onKeyDown={e => {
                        if (e.key === "Enter" || e.key === "Tab") {
                            if (draft.trim()) { e.preventDefault(); commit(draft); }
                        } else if (e.key === "Backspace" && !draft && list.length) {
                            removeAt(list.length - 1);
                        }
                    }}
                    onBlur={() => commit(draft)}
                    className="flex-1 min-w-[8rem] text-sm bg-transparent dark:text-white px-1 py-0.5 focus:outline-none"
                />
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">Press Enter or comma to add</p>
                {unusedExamples.map(ex => (
                    <button key={ex} type="button" onClick={() => commit(ex)}
                        className="text-xs px-1.5 py-0.5 rounded border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-indigo-400 hover:text-indigo-500">
                        + {ex}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default TagsInput;
